<template>
  <layout :title="pageTitle">
    <section class="section">
      <div class="columns">
        <div class="column is-3">
          <ul class="inner-menu box">
            <li>
              <ScrollLink to="#overview">Overview</ScrollLink>
            </li>
            <li>
              <ScrollLink to="#email-log">Email Log</ScrollLink>
            </li>
            <li>
              <ScrollLink to="#defaults">Edit Defaults</ScrollLink>
            </li>
            <li class="submenu">
              <ScrollLink to="#defaultsDetails">Details</ScrollLink>
              <ScrollLink to="#reports">Reports</ScrollLink>
              <ScrollLink to="#welcomes">Welcomes</ScrollLink>
              <ScrollLink to="#emails">Emails</ScrollLink>
              <ScrollLink to="#relationships">Relationships</ScrollLink>
            </li>
            <li>
              <ScrollLink to="#participants">Participants</ScrollLink>
            </li>
            <li>
              <ScrollLink to="#feedbackGivers">Feedback Givers</ScrollLink>
            </li>
            <li>
              <ScrollLink to="#actions">Actions</ScrollLink>
            </li>
          </ul>
        </div>

        <div class="column is-9">
          <div class="box">
            <a id="overview"></a>
            <div class="title-bar">
              <h3>Overview</h3>
              <div class="buttons">
                <b-icon
                  pack="fas"
                  icon="sync"
                  size="is-small"
                  v-if="form.isSending"
                  custom-class="fa-spin level level-right"
                ></b-icon>
              </div>
            </div>
            <hr style="margin-top: 15px" />
            <form @submit.prevent="submit" v-on:change="submit">
              <div class="columns">
                <div class="column">
                  <b-field
                    label="Project Name"
                    :type="{ 'is-danger': errors?.name }"
                    :message="errors?.name"
                  >
                    <b-input v-model="form.name"></b-input>
                  </b-field>
                </div>
                <div class="column">
                  <b-field
                    label="Client"
                    :type="{
                      'is-danger': errors?.client_id,
                    }"
                    :message="errors?.client_id"
                  >
                    <b-select
                      v-model="form.client_id"
                      placeholder="Select a name"
                      expanded
                    >
                      <option
                        v-for="client in clients"
                        :value="client.id"
                        :key="client.id"
                      >
                        {{ client.name }}
                      </option>
                    </b-select>
                  </b-field>
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <b-field
                    label="Project Group"
                    :type="{
                      'is-danger': errors?.project_group_id,
                    }"
                    :message="errors?.project_group_id"
                  >
                    <b-select
                      v-model="form.project_group_id"
                      placeholder="Select a project group"
                      expanded
                    >
                      <option
                        v-for="group in groups"
                        :value="group.id"
                        :key="group.id"
                      >
                        {{ group.name }}
                      </option>
                    </b-select>
                  </b-field>
                </div>
                <div class="column">
                  <b-field
                    label="Question Set"
                    :type="{
                      'is-danger': errors?.question_set_id,
                    }"
                    :message="errors?.question_set_id"
                  >
                    <div v-if="project.project_group_id !== null">
                      {{ project.group.question_set.name }}
                    </div>
                    <b-select
                      v-else
                      v-model="form.question_set_id"
                      placeholder="Select a question set"
                      expanded
                    >
                      <option
                        v-for="set in project.client.question_sets"
                        :value="set.id"
                        :key="set.id"
                      >
                        {{ set.name }}
                      </option>
                    </b-select>
                  </b-field>
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <b-field
                    label="Start Date"
                    :type="{
                      'is-danger': errors?.start_date,
                    }"
                    :message="errors?.start_date"
                  >
                    <b-datepicker
                      v-model="form.start_date"
                      @input="submit"
                      placeholder="Select a start date"
                      trap-focus
                    >
                    </b-datepicker>
                  </b-field>
                </div>
                <div class="column">
                  <b-field
                    label="Initial End Date"
                    :type="{
                      'is-danger': errors?.soft_end_date,
                    }"
                    :message="errors?.soft_end_date"
                  >
                    <b-datepicker
                      v-model="form.soft_end_date"
                      :min-date="form.start_date"
                      @input="submit"
                      placeholder="Select an initial end date"
                      trap-focus
                    >
                    </b-datepicker>
                  </b-field>
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <b-field
                    label="Final End Date"
                    :type="{ 'is-danger': errors?.end_date }"
                    :message="errors?.end_date"
                  >
                    <b-datepicker
                      v-model="form.end_date"
                      @input="submit"
                      :min-date="form.start_date"
                      placeholder="Select a final end date"
                      trap-focus
                    >
                    </b-datepicker>
                  </b-field>
                </div>
              </div>
            </form>
          </div>

          <div class="box">
            <a id="overview"></a>
            <div class="title-bar">
              <h3>Email Dates</h3>
              <b-field>
                <b-checkbox
                  v-model="settingsForm.enableAutomatedEmails"
                  type="is-primary"
                  @input="submitSettings"
                >
                  Enable automated emails
                </b-checkbox>
              </b-field>
            </div>
            <hr style="margin-top: 15px" />
            <b-notification
              v-if="showDatesWarning"
              type="is-warning is-light"
              aria-close-label="Close notification"
              role="alert"
              :closable="false"
            >
              Automated emails are disabled, so changing the email dates will
              have no affect on the project.
              <a
                href="#"
                class="is-underlined"
                type="is-text"
                @click="
                  (e) => {
                    e.preventDefault();
                    settingsForm.enableAutomatedEmails = true;
                    submitSettings();
                  }
                "
                >Enable automated emails
              </a>
            </b-notification>
            <div class="columns">
              <div class="column">
                <b-field label="Welcome Date">
                  <b-datepicker
                    v-model="settingsForm.welcomeDate"
                    :show-week-number="showWeekNumber"
                    :min-date="form.start_date"
                    :locale="locale"
                    @input="submitSettings"
                    placeholder="Click to select..."
                    trap-focus
                  >
                  </b-datepicker>
                </b-field>
              </div>
              <div class="column">
                <b-field label="Chase Date">
                  <b-datepicker
                    v-model="settingsForm.chaseDate"
                    :show-week-number="showWeekNumber"
                    :min-date="settingsForm.welcomeDate"
                    :locale="locale"
                    @input="submitSettings"
                    placeholder="Click to select..."
                    trap-focus
                  >
                  </b-datepicker>
                </b-field>
              </div>
            </div>
            <div class="columns">
              <div class="column">
                <b-field label="2nd Chase Date">
                  <b-datepicker
                    v-model="settingsForm.secondChaseDate"
                    :show-week-number="showWeekNumber"
                    :min-date="settingsForm.chaseDate"
                    :locale="locale"
                    @input="submitSettings"
                    placeholder="Click to select..."
                    trap-focus
                  >
                  </b-datepicker>
                </b-field>
              </div>
              <div class="column">
                <b-field label="Deadline Extension Date">
                  <b-datepicker
                    v-model="settingsForm.deadlineExtensionDate"
                    :show-week-number="showWeekNumber"
                    :min-date="settingsForm.secondChaseDate"
                    :locale="locale"
                    @input="submitSettings"
                    placeholder="Click to select..."
                    trap-focus
                  >
                  </b-datepicker>
                </b-field>
              </div>
            </div>
          </div>

          <div class="box">
            <a class="anchor" id="email-log"></a>
            <h3>Email Log</h3>
            <hr style="margin-top: 15px" />
            <div class="columns">
              <div class="column">
                <email-log-table :emailLogs="project.email_logs" is-project />
              </div>
            </div>
          </div>

          <ManageDefaults
            :defaults="defaults"
            :relationships="project.relationships"
            :entity="project"
            entityType="App\Models\Project"
          />

          <a id="participants"></a>
          <div class="box">
            <div class="title-bar">
              <h3>Participants</h3>
              <div class="buttons">
                <inertia-link
                  :href="
                    route('projects.setup.participants', {
                      project: project.unique_id,
                      redirect: encodeURIComponent(
                        route('projects.show', project.unique_id)
                      ),
                    })
                  "
                >
                  <b-button class="is-primary"> Edit </b-button>
                </inertia-link>
              </div>
            </div>
            <hr />
            <div v-if="loadingUsers">
              <i>Loading <a href="#" @click="loadProgress">Manual Reload</a></i>
            </div>
            <div v-else-if="participants">
              <ViewParticipants
                :participants="participants"
                :project="project"
                @refresh="loadProgress"
              />
            </div>
            <div class="empty-box" v-else>
              <div class="icon">
                <b-icon pack="fas" icon="users" size="is-small"></b-icon>
              </div>
              <p>This project has no participants.</p>
              <inertia-link
                :href="
                  route('projects.setup.participants', {
                    project: project.unique_id,
                    redirect: encodeURIComponent(
                      route('projects.show', project.unique_id)
                    ),
                  })
                "
              >
                <b-button class="is-primary"> Add or Create </b-button>
              </inertia-link>
            </div>
          </div>

          <a id="feedbackGivers"></a>
          <div class="box">
            <div class="title-bar">
              <h3>Feedback Givers</h3>
              <div class="buttons">
                <inertia-link
                  :href="
                    route('projects.setup.feedbackGivers', {
                      project: project.unique_id,
                      redirect: encodeURIComponent(
                        route('projects.show', project.unique_id)
                      ),
                    })
                  "
                >
                  <b-button class="is-primary"> Edit </b-button>
                </inertia-link>
              </div>
            </div>
            <hr />
            <div v-if="loadingUsers">
              <i>Loading <a href="#" @click="loadProgress">Manual Reload</a></i>
            </div>
            <div v-else-if="feedbackGivers">
              <ViewFeedbackGivers
                :feedbackGivers="feedbackGivers"
                :project="project"
                @refresh="loadProgress"
              />
            </div>
            <div class="empty-box" v-else>
              <div class="icon">
                <b-icon pack="fas" icon="user-friends" size="is-small"></b-icon>
              </div>
              <p>This project has no feedback givers.</p>
              <inertia-link
                :href="
                  route('projects.setup.feedbackGivers', {
                    project: project.unique_id,
                    redirect: encodeURIComponent(
                      route('projects.show', project.unique_id)
                    ),
                  })
                "
              >
                <b-button class="is-primary"> Add or Create </b-button>
              </inertia-link>
            </div>
          </div>

          <div class="box">
            <a class="anchor" id="actions"></a>
            <h3>Actions</h3>
            <hr style="margin-top: 15px" />
            <b-button @click="showDeleteModal = true" class="is-danger"
              >Delete Project</b-button
            >
            <inertia-link :href="route('duplicate.project', project.unique_id)">
              <b-button class="is-info"> Clone </b-button>
            </inertia-link>
            <b-button
              @click="showDownloadReportsModal = true"
              class="is-warning"
              >Reports</b-button
            >
            <hr />
            <div class="is-flex is-justify-content-space-between">
              <strong>Project Emails</strong>
              <b-button
                @click="composeModal"
                size="is-small"
                type="is-primary"
                class="mr-3"
                icon-left="plus"
                outlined
              >
                Compose Email
              </b-button>
            </div>
            <b-table :data="emailTypes" hoverable>
              <b-table-column field="label" v-slot="props">
                {{ props.row.label }}
              </b-table-column>

              <b-table-column v-slot="props" width="40" cell-class="is-flex">
                <b-tooltip
                  :label="`Send a test to ${$page.props.auth.user.email}`"
                  position="is-left"
                  size="is-small"
                >
                  <b-button
                    v-if="props.row.testable"
                    type="is-text"
                    size="is-small"
                    @click="sendTestEmail(props.row.key)"
                    >Send Test</b-button
                  >
                </b-tooltip>
                <b-button
                  type="is-dark"
                  size="is-small"
                  class="ml-auto"
                  @click="sendEmail(props.row.key)"
                  >Send Email</b-button
                >
              </b-table-column>
            </b-table>
          </div>
        </div>
      </div>
    </section>

    <!-- modals -->
    <b-modal
      v-model="showDeleteModal"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-modal
    >
      <DeleteModal
        :delete-route="route('projects.destroy', project.unique_id).toString()"
        :name="project.name"
        :message="'You are about to delete a project.'"
      />
    </b-modal>

    <b-modal
      v-model="showDownloadReportsModal"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-modal
    >
      <DownloadReportsModal :project="project" />
    </b-modal>
  </layout>
</template>

<script>
import Layout from './Layout';
import ScrollLink from '@/Components/Shared/ScrollLink';
import ViewParticipants from '../../Components/Management/ViewParticipants';
import ViewFeedbackGivers from '../../Components/Management/ViewFeedbackGivers';
import ManageDefaults from '../../Components/Management/ManageDefaults';
import DownloadReportsModal from '../../Components/Management/DownloadReportsModal';
import DeleteModal from '../../Components/Management/DeleteModal';
import UserChecklistModal from '@/Components/Management/Modals/UserChecklistModal';
import EmailLogTable from '@/Components/Management/EmailLogTable';
import ManualEmailModal from '@/Components/Management/Modals/ManualEmailModal';
import * as dayjs from 'dayjs';

export default {
  components: {
    Layout,
    ScrollLink,
    ViewParticipants,
    ViewFeedbackGivers,
    ManageDefaults,
    DownloadReportsModal,
    DeleteModal,
    EmailLogTable,
  },
  props: {
    project: Object,
    clients: Array,
    settings: Object,
    defaults: Object,
    groups: Array,
    errors: Object,
  },
  data() {
    const softDate = transformDate(this.project.soft_end_date);
    const welcomeDate = transformDate(this.settings.welcomeDate.value);
    const chaseDate = transformDate(this.settings.chaseDate.value);
    const secondChaseDate = transformDate(this.settings.secondChaseDate.value);
    const deadlineExtensionDate = transformDate(
      this.settings.deadlineExtensionDate.value
    );

    return {
      pageTitle: 'Edit Project: ' + this.project.name,
      settingsForm: {
        welcomeDate,
        chaseDate,
        secondChaseDate,
        deadlineExtensionDate,
        enableAutomatedEmails:
          this.settings.enableAutomatedEmails.value === '1',
      },
      form: {
        name: this.project.name,
        client_id: this.project.client_id,
        project_group_id: this.project.project_group_id,
        question_set_id: this.project.question_set_id,
        start_date: new Date(this.project.start_date),
        end_date: new Date(this.project.end_date),
        soft_end_date: softDate,
        isSending: false,
      },
      emailTypes: [
        { label: 'Welcome', key: 'welcome', testable: true },
        { label: 'Chase', key: 'chase', testable: true },
        { label: 'Second Chase', key: 'secondChase', testable: true },
        { label: 'Extension', key: 'extension', testable: true },
      ],
      showDownloadReportsModal: false,
      showDeleteModal: false,
      showWeekNumber: false,
      locale: 'en-GB',
      participants: null,
      feedbackGivers: null,
      loadingUsers: true,
    };
  },
  computed: {
    showDatesWarning() {
      return !this.settingsForm.enableAutomatedEmails;
    },
  },
  methods: {
    emailToast() {
      this.$buefy.toast.open({
        message: 'Email Send',
        type: 'is-success',
      });
    },
    sendTestEmail(template) {
      this.$axios
        .get(route('emails.test', [this.project.unique_id, template]))
        .then((_) => {
          this.$buefy.toast.open({
            message: `A test email was sent to <strong class="has-text-white">${this.$page.props.auth.user.email}</strong>`,
            type: 'is-success',
            position: 'is-bottom',
            duration: 4000,
          });
        })
        .catch((err) => {
          console.log(err);
          this.$buefy.toast.open({
            message: 'Something went wrong. Please try again later.',
            type: 'is-danger',
            position: 'is-bottom',
          });
        });
    },
    async sendEmail(template) {
      const response = await this.$axios.get(
        route('emails.recipients', [this.project.unique_id, template])
      );
      let users = response.data.map((user) => {
        user['checked'] = user.validated;
        delete user.validated;
        return user;
      });
      this.$buefy.modal.open({
        parent: this,
        component: UserChecklistModal,
        props: {
          users: users,
        },
        events: {
          confirmed: (checkedUsers) => {
            this.$inertia.post(
              route('emails.default', [this.project.unique_id, template]),
              {
                users: checkedUsers,
              }
            );
            this.emailToast();
          },
        },
        hasModalCard: true,
        trapFocus: true,
      });
    },
    submit() {
      console.log(this.form.start_date);
      console.log(this.formatDateToIsoString(this.form.start_date));

      const data = {
        name: this.form.name,
        client_id: this.form.client_id,
        project_group_id: this.form.project_group_id,
        question_set_id: this.form.question_set_id,
        start_date: this.formatDateToIsoString(this.form.start_date),
        end_date: this.formatDateToIsoString(this.form.end_date),
        soft_end_date: this.formatDateToIsoString(this.form.soft_end_date),
      };
      this.$inertia.patch(
        route('projects.update', this.project.unique_id),
        data,
        {
          preserveScroll: true,
          onStart: () => (this.form.isSending = true),
          onFinish: () => {
            if (Object.keys(this.errors).length) {
              this.$buefy.toast.open({
                message: 'There was an error updating the project',
                type: 'is-danger',
                position: 'is-top',
              });
            } else {
              this.$buefy.toast.open({
                message: 'Project information updated',
                type: 'is-success',
                position: 'is-top',
              });
            }
            this.form.isSending = false;
          },
          onError: () => {},
        }
      );
    },
    submitSettings() {
      const data = {
        welcomeDate:
          this.settingsForm.welcomeDate?.toISOString().slice(0, 10) ?? null,
        chaseDate:
          this.settingsForm.chaseDate?.toISOString().slice(0, 10) ?? null,
        secondChaseDate:
          this.settingsForm.secondChaseDate?.toISOString().slice(0, 10) ?? null,
        deadlineExtensionDate:
          this.settingsForm.deadlineExtensionDate?.toISOString().slice(0, 10) ??
          null,
        enableAutomatedEmails: this.settingsForm.enableAutomatedEmails,
      };
      this.$inertia.patch(
        route('projects.updateSettings', this.project.unique_id),
        data,
        {
          preserveScroll: true,
          onStart: () => (this.form.isSending = true),
          onFinish: () => {
            if (Object.keys(this.errors).length) {
              this.$buefy.toast.open({
                message: 'There was an error updating the project',
                type: 'is-danger',
                position: 'is-top',
              });
            } else {
              this.$buefy.toast.open({
                message: 'Project information updated',
                type: 'is-success',
                position: 'is-top',
              });
            }
            this.form.isSending = false;
          },
          onError: () => {},
        }
      );
    },
    loadProgress() {
      let url = route('projects.userProgress', this.project.unique_id);

      fetch(url, {
        method: 'get',
      })
        .then((response) => response.json())
        .then((data) => {
          this.participants = data.participants;
          this.feedbackGivers = data.feedbackGivers;
          this.loadingUsers = false;
        });
    },
    formatDateToIsoString(date) {
      if (!date) return null;
      return dayjs(date).format('YYYY-MM-DD');
    },
    composeModal() {
      const participants = this.participants.map((p) => {
        return {
          ...p,
          isParticipant: true,
          isFeedbackGiver: this.feedbackGivers.some((f) => f.id === p.id),
        };
      });
      const feedbackGivers = this.feedbackGivers.map((f) => {
        return {
          ...f,
          isParticipant: this.participants.some((p) => p.id === f.id),
          isFeedbackGiver: true,
        };
      });
      this.$buefy.modal.open({
        parent: this,
        component: ManualEmailModal,
        props: {
          users: [...participants, ...feedbackGivers],
          project: this.project.unique_id,
        },
        fullScreen: false,
        trapFocus: true,
        canCancel: ['x', 'escape'],
      });
    },
  },
  mounted() {
    this.loadProgress();
  },
};

function transformDate(date) {
  if (!date) {
    return null;
  }

  return new Date(date);
}
</script>
